/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BEM, someIncludesCaseInsensitive } from "@adapptio/lib-components";
import { ResourceService } from "../../resources/ResourceService";
import { IResourceDescription, RESOURCE_TYPES } from "@adapptio/lib-core";
import { isFunction, isNonEmptyString, offEvent, onEvent } from "@adapptio/lib-shared";
import { SearchBox, SEARCHBOX_SIZE, SEARCHBOX_STYLE } from "./SearchBox";
import { IResourceTreeState } from "../../resources/IResourceTreeState";
import { ICON_SIZE } from "./Icon";
import { TEditorResource } from "../../resources/ResourceTypes";
import { ITreeViewProps, TreeView } from "./Tree/TreeView";
import { useTreeState } from "./Tree/useTreeState";
import { ITreeNode, ITreeNodeAction, ITreeState } from "./Tree/TreeViewTypes";
import { DROPDOWN_STYLE, IDropdownMenuItem } from "./DropdownMenu";
import { useEditorMainContext } from "../contexts/EditorMainContext";
import { useDeepCompareCallback } from "use-deep-compare";
import { ABILITY_ACTIONS, AbilityService } from "../../ability/AbilityService";
import { IAppInfo } from "../../common/AppInfo";
import { getResourceTypeData } from "../../common/ResourceHelpers";

export enum RESOURCE_LIST_STYLE {
	DEFAULT = "default",
	COMPACT = "compact"
}

export interface IResourceNode {
	id: string;
	name: string;
	resourceType: RESOURCE_TYPES;
	basePath: string;
	isOpened: boolean;
	canWrite: boolean;
	canRename: boolean;
	canDelete: boolean;
	isValid?: boolean;
	isRoot?: boolean;
	children: IResourceNode[];
}

function isDirectory(resourceDescription: IResourceDescription | IResourceNode): boolean {
	return resourceDescription.resourceType === RESOURCE_TYPES.DIRECTORY;
}

interface IResourceListFilter {
	search?: string;
	allowedResourceTypes?: RESOURCE_TYPES[];
	showRoot?: boolean;
	/** Base path to filter resources - only shows children of this path */
	basePath?: string;
}

/**
 * Builds a resource tree based on resource list and state
 *
 * @param appInfo
 * @param resourceList Resource list
 * @param abilityService Ability service
 * @param resourceTreeState Resource tree state
 * @param filter Filter
 */
function buildResourceTree(
	{
		appInfo,
		resourceList,
		abilityService
	}: { appInfo: IAppInfo; resourceList: TEditorResource[]; abilityService: AbilityService },
	resourceTreeState: IResourceTreeState,
	filter: IResourceListFilter
): IResourceNode[] {
	const rootVisible = filter?.showRoot || filter?.allowedResourceTypes?.includes(RESOURCE_TYPES.DIRECTORY);

	// Build index
	const itemIndex: { [K: string]: IResourceDescription } = {};

	const filteredFullItemPaths = new Set<string>();
	const hiddenPaths = new Set<string>();

	// Normalize basePath - ensure consistent format (no trailing slash, empty string for root)
	const filterBasePath = filter.basePath ? filter.basePath.replace(/\/$/, "") : "";

	processItems: for (let i = 0; i < resourceList.length; i++) {
		const item = resourceList[i].descriptor;
		const fullItemPath = `${item.basePath}${item.basePath && "/"}${item.name}`;

		// Store every item by path
		itemIndex[fullItemPath] = item;

		// Filter by basePath - only include items that are children of the basePath
		if (filterBasePath) {
			// Item must be directly in basePath or in a subdirectory of basePath
			if (item.basePath !== filterBasePath && !item.basePath.startsWith(filterBasePath + "/")) {
				continue;
			}
		}

		// Skip items in hidden directories
		if (hiddenPaths.has(item.basePath)) {
			if (isDirectory(item)) {
				hiddenPaths.add(fullItemPath);
			}

			continue;
		}

		// Check if user is allowed to read this resource
		if (!abilityService.can(ABILITY_ACTIONS.READ, item)) {
			if (isDirectory(item)) {
				hiddenPaths.add(fullItemPath);
			}

			continue;
		}

		// Filter by resource type
		if (
			filter.allowedResourceTypes?.length > 0 &&
			!filter.allowedResourceTypes.includes(item.resourceType) &&
			!isDirectory(item)
		) {
			continue;
		}

		// Apply search
		if (
			isNonEmptyString(filter.search) &&
			!someIncludesCaseInsensitive(filter.search, [ item.name, item.id ])
		) {
			continue;
		}

		// Process parent paths (but only those after basePath)
		if (isNonEmptyString(item.basePath) && !filteredFullItemPaths.has(item.basePath)) {
			const pathLevels = item.basePath.split("/");
			let currentParentPath = "";

			for (let i = 0; i < pathLevels.length; i++) {
				currentParentPath = `${currentParentPath}${currentParentPath && "/"}${pathLevels[i]}`;

				if (hiddenPaths.has(currentParentPath)) {
					continue processItems;
				}

				// Only add parent paths that are after the basePath
				if (!filterBasePath || currentParentPath.startsWith(filterBasePath + "/")) {
					filteredFullItemPaths.add(currentParentPath);
				}
			}
		}

		// Store this item's path
		filteredFullItemPaths.add(fullItemPath);
	}

	const nodeIndex: { [K: string]: IResourceNode } = {};
	const rootNodes: IResourceNode[] = [];

	const processedIds = new Map();

	const addNode = (key: string): IResourceNode => {
		if (nodeIndex[key]) {
			return nodeIndex[key];
		}

		let node: IResourceNode;

		// Create from resource descriptor
		if (itemIndex[key]) {
			const item = itemIndex[key];

			// Prevent duplicate ids
			if (processedIds.has(item.id)) {
				return nodeIndex[processedIds.get(item.id)];
			}

			node = {
				id: item.id,
				name: item.name,
				basePath: item.basePath,
				resourceType: item.resourceType,
				isOpened: resourceTreeState.resources[key]?.isOpened || false,
				isValid: item.parseData?.valid || false,
				// @todo protected items
				canWrite: abilityService.can(ABILITY_ACTIONS.WRITE, item),
				canRename: abilityService.can(ABILITY_ACTIONS.RENAME, item),
				canDelete: abilityService.can(ABILITY_ACTIONS.REMOVE, item),
				children: []
			};

			// Create unknown node
		} else {
			const path = key.split("/");
			const name = path.pop();

			node = {
				id: "unknown:" + key,
				name: name,
				basePath: path.join("/"),
				resourceType: RESOURCE_TYPES.UNKNOWN,
				isOpened: resourceTreeState.resources[key]?.isOpened || false,
				isValid: true,
				canWrite: false,
				canRename: false,
				canDelete: false,
				children: []
			};
		}

		// Add to index
		nodeIndex[key] = node;

		processedIds.set(node.id, key);

		// Add to root or to a parent
		// When basePath filter is set, items directly in that basePath are treated as root
		const isRootNode = filterBasePath
			? node.basePath === filterBasePath
			: node.basePath === "";

		if (isRootNode) {
			rootNodes.push(node);
		} else {
			const parent = addNode(node.basePath);
			parent.children.push(node);

			if (node.isOpened) {
				parent.isOpened = true;
			}
		}

		return node;
	};

	for (const itemKey of filteredFullItemPaths) {
		addNode(itemKey);
	}

	// Sort children
	const sortChildren = (node: IResourceNode) => {
		if (!node.children) {
			return;
		}

		node.children.sort((a, b) => {
			if (a.resourceType === RESOURCE_TYPES.DIRECTORY && b.resourceType !== RESOURCE_TYPES.DIRECTORY) {
				return -1;
			} else if (a.resourceType !== RESOURCE_TYPES.DIRECTORY && b.resourceType === RESOURCE_TYPES.DIRECTORY) {
				return 1;
			} else {
				return a.name.localeCompare(b.name);
			}
		});

		for (let i = 0; i < node.children.length; i++) {
			sortChildren(node.children[i]);
		}
	}

	for (let i = 0; i < rootNodes.length; i++) {
		sortChildren(rootNodes[i]);
	}

	if (rootVisible) {
		const root = [
			{
				id: '__root__',
				name: appInfo.name,
				basePath: "",
				resourceType: RESOURCE_TYPES.DIRECTORY,
				isOpened: true,
				isValid: true,
				isRoot: true,
				canWrite: false,
				canRename: false,
				canDelete: false,
				children: rootNodes
			}
		];

		return root;
	}

	return rootNodes;
}

/**
 * Returns filtered resource tree
 *
 * @param appInfo
 * @param resourceService Resource service
 * @param abilityService
 * @param filter Filter
 */
function getResourceTree(
	{
		appInfo,
		resourceService,
		abilityService
	}: { appInfo: IAppInfo; resourceService: ResourceService; abilityService: AbilityService },
	filter: IResourceListFilter
): IResourceNode[] {
	const resourceList = resourceService.getResourceList().slice();

	return buildResourceTree({ appInfo, resourceList, abilityService }, { resources: {} }, filter);
}

/**
 * Component props
 */
export interface IResourceListProps {
	isActive?: boolean;
	onSelect: (node: IResourceNode) => void;
	selectedId?: string;
	allowedResourceTypes?: RESOURCE_TYPES[];
	isDisabled?: (node: IResourceNode) => boolean;
	isSelectable?: (node: IResourceNode) => boolean;
	style?: RESOURCE_LIST_STYLE;
	onNodeDragDrop?: ITreeViewProps["onNodeDragDrop"];
	showRoot?: boolean;
	labelSearch?: string;
	treeState?: ITreeState;
	onTreeStateChange?: (treeState: ITreeState) => void;
	/** Base path to filter resources - only shows children of this path */
	basePath?: string;
	/** Hide the search bar */
	hideSearch?: boolean;
	/** Whether to expand all tree nodes by default */
	defaultExpandAll?: boolean;
}

/**
 * Resource class list
 *
 * @param params
 */
export const ResourceList: React.FunctionComponent<IResourceListProps> = ({
	isActive,
	onSelect,
	selectedId,
	allowedResourceTypes,
	style,
	isSelectable,
	isDisabled,
	onNodeDragDrop,
	showRoot,
	labelSearch,
	treeState: treeStateProp,
	onTreeStateChange,
	basePath,
	hideSearch,
	defaultExpandAll = true
}) => {
	const editorCtx = useEditorMainContext();
	const { resourceService, appInfo, abilityService } = editorCtx;

	const [ resourceTree, setResourceTree ] = useState(() =>
		getResourceTree(
			{ appInfo, resourceService, abilityService },
			{ search: "", allowedResourceTypes, showRoot, basePath }
		)
	);
	const [ searchValue, setSearchValue ] = useState("");

	const selections = useMemo(() => [ selectedId ], [ selectedId ]);

	const handleSearch = (search: string) => {
		setSearchValue(search);
		setResourceTree(
			getResourceTree(
				{ appInfo, resourceService, abilityService },
				{ search, allowedResourceTypes, showRoot, basePath }
			)
		);
	};

	const [ localTreeState, setTreeState ] = useTreeState();
	const treeState = treeStateProp || localTreeState;
	const treeStateUpdateHandler = onTreeStateChange || setTreeState;

	const isResourceNodeDisabled = useDeepCompareCallback<(resourceNode: IResourceNode) => boolean>(
		(resourceNode) => {
			if (isFunction(isDisabled)) {
				return isDisabled(resourceNode);
			}

			return false;
		},
		[ isDisabled ]
	);

	const isResourceNodeSelectable = useDeepCompareCallback<(resourceNode: IResourceNode) => boolean>(
		(resourceNode) => {
			if (isFunction(isSelectable)) {
				return isSelectable(resourceNode);
			}

			return !allowedResourceTypes || allowedResourceTypes?.includes(resourceNode.resourceType);
		},
		[ allowedResourceTypes, isSelectable ]
	);

	const mapResourceNodeToTreeNode = useCallback<(resourceNode: IResourceNode) => ITreeNode>(
		(resourceNode: IResourceNode) => {
			const { icon, typeMod } = getResourceTypeData(resourceNode.resourceType);

			const menuItems: IDropdownMenuItem[] = [];

			if (resourceNode.canRename) {
				menuItems.push({
					id: "rename",
					label: "Rename",
					icon: "mdi/pencil"
				});

				menuItems.push({
					id: "move",
					label: "Move",
					icon: "mdi/folder-move"
				});
			}

			if (resourceNode.resourceType !== RESOURCE_TYPES.DIRECTORY) {
				menuItems.push({
					id: "duplicate",
					label: "Duplicate",
					icon: "mdi/content-copy"
				});
			}

			if (
				!(
					resourceNode.resourceType === RESOURCE_TYPES.INTEGRATION ||
					resourceNode.resourceType === RESOURCE_TYPES.DIRECTORY
				)
			) {
				menuItems.push({
					id: "export",
					label: "Export to YAML",
					icon: "mdi/export"
				});
			}

			if (resourceNode.canDelete) {
				menuItems.push({
					id: "delete",
					label: "Delete",
					icon: "mdi/delete"
				});
			}

			if (resourceNode.resourceType === RESOURCE_TYPES.DIRECTORY) {
				menuItems.push({
					id: "import",
					label: "Import from YAML",
					icon: "mdi/import"
				});
				menuItems.push({
					id: "createSubDir",
					label: "Create subdirectory",
					icon: "mdi/folder"
				});
				menuItems.push({
					id: "createSubView",
					label: "Create view",
					icon: "mdi/view-dashboard"
				});
				menuItems.push({
					id: "createSubAction",
					label: "Create action",
					icon: "mdi/motion-play"
				});
				menuItems.push({
					id: "createSubIntegration",
					label: "Create integration",
					icon: "mdi/connection"
				});
				menuItems.push({
					id: "createSubEndpoint",
					label: "Create endpoint",
					icon: "mdi/hubspot"
				});
				menuItems.push({
					id: "createSubAsset",
					label: "Create asset",
					icon: "mdi/file"
				});
				menuItems.push({
					id: "createSubReadme",
					label: "Create readme",
					icon: "mdi/file-document"
				});
				menuItems.push({
					id: "createSubTheme",
					label: "Create theme",
					icon: "mdi/shape-outline"
				});
			}

			menuItems.push({
				id: "copyPath",
				label: "Copy Path",
				icon: "mdi/content-copy"
			});

			const treeNodeData: ITreeNode = {
				id: resourceNode.id,
				label: resourceNode.name,
				icon,
				children: resourceNode.children.map((item) => mapResourceNodeToTreeNode(item)),
				disabled: isResourceNodeDisabled(resourceNode),
				selectable: isResourceNodeSelectable(resourceNode),
				actions: [
					{
						icon: "mdi/dots-horizontal",
						className: "edt-resource-list__item-action",
						hasPrimaryAction: false,
						menuItems: menuItems,
						menuStyle: DROPDOWN_STYLE.COMPACT
					}
				],
				isLeaf: !isDirectory(resourceNode),
				metaData: {
					resourceNode,
					typeMod
				}
			};

			return treeNodeData;
		},
		[ isResourceNodeSelectable, abilityService ]
	);

	const treeItems = useMemo<ITreeViewProps["items"]>(
		() =>
			resourceTree
				.sort((a, b) => {
					if (a?.resourceType == b?.resourceType) {
						return a?.name?.localeCompare(b?.name);
					}

					return a?.resourceType == RESOURCE_TYPES.DIRECTORY ? -1 : 0;
				})
				.map((item) => mapResourceNodeToTreeNode(item)),
		[ resourceTree, mapResourceNodeToTreeNode ]
	);

	const handleNodeSelect = useCallback<ITreeViewProps["onNodeSelect"]>(
		(treeNode) => {
			if (isFunction(onSelect)) {
				onSelect(treeNode.metaData.resourceNode as IResourceNode);
			}
		},
		[ onSelect ]
	);

	const handleNodeActionMenuItemClick = useCallback(
		(node: ITreeNode, _action: ITreeNodeAction, menuItem: IDropdownMenuItem) => {
			const basePath = node.id
				? editorCtx.resourceService.getResourceNormalizedPathById(`${node.id}`)
				: "";

			switch (menuItem.id) {
				case "copyPath": {
					navigator.clipboard.writeText(basePath).catch((err) => {
						console.error("Failed to copy path to clipboard:", err);
					});
					break;
				}
				case "rename": {
					editorCtx.commandsService.execute("resource.rename", node.id).catch((err) => {
						console.error("Failed to execute resource rename command:", err);
					});
					break;
				}
				case "move": {
					editorCtx.commandsService.execute("resource.moveDialog.open", node.id).catch((err) => {
						console.error("Failed to execute resource move command:", err);
					});
					break;
				}
				case "duplicate": {
					editorCtx.commandsService.execute("resource.duplicate", node.id).catch((err) => {
						console.error("Failed to execute resource duplicate command:", err);
					});
					break;
				}
				case "export": {
					editorCtx.commandsService.execute("resource.export", node.id).catch((err) => {
						console.error("Failed to execute resource export command:", err);
					});
					break;
				}
				case "import": {
					editorCtx.commandsService.execute("importBlueprint.open", node.id).catch((err) => {
						console.error("Failed to execute resource import command:", err);
					});
					break;
				}
				case "delete": {
					editorCtx.commandsService.execute("resource.delete", node.id).catch((err) => {
						console.error("Failed to execute resource delete command:", err);
					});
					break;
				}
				case "createSubDir": {
					editorCtx.commandsService
						.execute("resource.create.subdirectory", node.id)
						.catch((err) => {
							console.error("Failed to execute create subdirectory command:", err);
						});
					break;
				}
				case "createSubView": {
					const createCommandId = editorCtx.resourceService.getResourceCreateCommandId(
						RESOURCE_TYPES.VIEW
					);

					editorCtx.commandsService.execute(createCommandId, basePath).catch((err) => {
						console.error("Failed to execute create sub view command:", err);
					});
					break;
				}
				case "createSubAction": {
					const createCommandId = editorCtx.resourceService.getResourceCreateCommandId(
						RESOURCE_TYPES.ACTION
					);

					editorCtx.commandsService.execute(createCommandId, basePath).catch((err) => {
						console.error("Failed to execute create sub action command:", err);
					});
					break;
				}
				case "createSubIntegration": {
					const createCommandId = editorCtx.resourceService.getResourceCreateCommandId(
						RESOURCE_TYPES.INTEGRATION
					);

					editorCtx.commandsService.execute(createCommandId, basePath).catch((err) => {
						console.error("Failed to execute create sub integration command:", err);
					});
					break;
				}
				case "createSubEndpoint": {
					const createCommandId = editorCtx.resourceService.getResourceCreateCommandId(
						RESOURCE_TYPES.ENDPOINT
					);

					editorCtx.commandsService.execute(createCommandId, basePath).catch((err) => {
						console.error("Failed to execute create sub endpoint command:", err);
					});
					break;
				}
				case "createSubAsset": {
					const createCommandId = editorCtx.resourceService.getResourceCreateCommandId(
						RESOURCE_TYPES.ASSET
					);

					editorCtx.commandsService.execute(createCommandId, basePath).catch((err) => {
						console.error("Failed to execute create sub asset command:", err);
					});
					break;
				}
				case "createSubReadme": {
					const createCommandId = editorCtx.resourceService.getResourceCreateCommandId(
						RESOURCE_TYPES.README
					);

					editorCtx.commandsService.execute(createCommandId, basePath).catch((err) => {
						console.error("Failed to execute create sub readme command:", err);
					});
					break;
				}
				case "createSubTheme": {
					const createCommandId = editorCtx.resourceService.getResourceCreateCommandId(
						RESOURCE_TYPES.THEME
					);

					editorCtx.commandsService.execute(createCommandId, basePath).catch((err) => {
						console.error("Failed to execute create sub theme command:", err);
					});
					break;
				}
			}
		},
		[ editorCtx.commandsService ]
	);

	useEffect(() => {
		const onResourceListChange = () =>
			setResourceTree(
				getResourceTree(
					{ appInfo, resourceService, abilityService },
					{
						search: searchValue,
						allowedResourceTypes,
						showRoot,
						basePath
					}
				)
			);

		onEvent(resourceService.onResourceListChange, onResourceListChange);

		return () => {
			offEvent(resourceService.onResourceListChange, onResourceListChange);
		};
	}, [ resourceService, searchValue, allowedResourceTypes, appInfo, showRoot, basePath ]);

	const baseClass = BEM("edt-resource-list", null, {
		style: style || RESOURCE_LIST_STYLE.DEFAULT
	});

	const getNodeClassName = useCallback<ITreeViewProps["getNodeClassName"]>((node) => {
		return `edt-resource-list__item--type-${(node.metaData.resourceNode as IResourceNode).resourceType}`;
	}, []);

	return (
		<div className={baseClass.toClassName()}>
			{!hideSearch && (
				<div className={baseClass.element("search").toClassName()}>
					<SearchBox
						value={searchValue}
						onChange={handleSearch}
						placeholder="Search..."
						label={labelSearch}
						size={SEARCHBOX_SIZE.SMALL}
						style={
							style === RESOURCE_LIST_STYLE.COMPACT
								? SEARCHBOX_STYLE.TRANSPARENT_NAKED
								: SEARCHBOX_STYLE.TRANSPARENT_NAKED_ROUND
						}
						shouldFocus={isActive}
					/>
				</div>
			)}
			<div className={baseClass.element("container").toClassName()}>
				<TreeView
					className={baseClass.element("tree").toClassName()}
					getNodeClassName={getNodeClassName}
					items={treeItems}
					state={treeState}
					onNodeSelect={handleNodeSelect}
					selections={selections}
					onStateUpdate={treeStateUpdateHandler}
					onNodeActionMenuItemClick={handleNodeActionMenuItemClick}
					switcherIconSize={ICON_SIZE.SMALL}
					defaultExpandAll={defaultExpandAll}
					sort={false}
					isDraggable={!!onNodeDragDrop}
					onNodeDragDrop={onNodeDragDrop}
				/>
			</div>
		</div>
	);
};
